import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, User } from "lucide-react";
import { coachGetMessages, coachSendMessage, coachTypingPing } from "../../lib/api";
import ChatConversation from "../../components/ChatConversation";

const POLL_MS = 5 * 1000;

export default function CoachConversationPage() {
  const { athleteId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () =>
      coachGetMessages(athleteId)
        .then(setData)
        .catch(() => setData((prev) => prev || { athlete: null, messages: [] }));
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [athleteId]);

  return (
    <div
      className="h-[calc(100vh-8rem)] md:h-[calc(100vh-4rem)] flex flex-col bg-[var(--tir-ivory)]"
      data-testid="coach-conversation-page"
    >
      <div className="border-b border-[var(--tir-border)] bg-white shrink-0">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/coaching/messages"
              className="text-[var(--tir-muted)] hover:text-[var(--tir-navy)]"
              data-testid="conversation-back"
              aria-label="Back to messages"
            >
              <ArrowLeft size={16} />
            </Link>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-navy)]">
                Conversation
              </div>
              <div className="font-serif-display text-xl sm:text-2xl text-[var(--tir-ink)] leading-tight truncate">
                {data?.athlete?.name || "…"}
              </div>
            </div>
          </div>
          <Link
            to={`/coaching/athletes/${athleteId}`}
            className="shrink-0 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)]"
            data-testid="conversation-athlete-link"
          >
            <User size={12} /> <span className="hidden sm:inline">Athlete page</span>
          </Link>
        </div>
      </div>
      <div className="flex-1 min-h-0 max-w-3xl w-full mx-auto">
        {data === null ? (
          <div className="text-center text-sm text-[var(--tir-muted)] italic py-10">
            Loading conversation…
          </div>
        ) : (
          <ChatConversation
            messages={data.messages}
            side="coach"
            onSend={(text, photo, audio) => coachSendMessage(athleteId, text, photo, audio)}
            otherTyping={!!data.other_typing}
            otherLabel={data.athlete?.name?.split(" ")[0] || "Athlete"}
            onTyping={() => coachTypingPing(athleteId)}
            contextLink={(m) =>
              m.program_id
                ? `/coaching/programs/${m.program_id}/edit?week=${m.week_number || 1}&workout=${m.workout_id}`
                : null
            }
          />
        )}
      </div>
    </div>
  );
}
